import { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faToggleOn, faToggleOff, faBell, faInfoCircle, faWarning } from '@fortawesome/free-solid-svg-icons'
import { LoadingSpinner } from '../../shared/components/ui/LoadingSpinner'

// Alert types from monorepo
enum AlertType {
  SENSOR_ALERTS = 'SENSOR_ALERTS',
  SYSTEM_CONTROL_MESSAGES = 'SYSTEM_CONTROL_MESSAGES',
  TEST_MESSAGES = 'TEST_MESSAGES',
}

const DEFAULT_ALERT_CONTROLS: Record<AlertType, boolean> = {
  [AlertType.SENSOR_ALERTS]: true,
  [AlertType.SYSTEM_CONTROL_MESSAGES]: false,
  [AlertType.TEST_MESSAGES]: false,
}

const alertTypeInfo = {
  [AlertType.SENSOR_ALERTS]: {
    icon: faBell,
    title: 'Sensor Alerts',
    description: 'Notifications for sensor threshold violations (high/low values, water detection, etc.)',
    color: 'text-red-600',
  },
  [AlertType.SYSTEM_CONTROL_MESSAGES]: {
    icon: faInfoCircle,
    title: 'System Control Messages',
    description: 'System status notifications and control messages from devices',
    color: 'text-blue-600',
  },
  [AlertType.TEST_MESSAGES]: {
    icon: faWarning,
    title: 'Test Messages',
    description: 'Test notifications for validating alert system functionality',
    color: 'text-yellow-600',
  },
}

export function AlertControls() {
  const [localControls, setLocalControls] = useState<Record<AlertType, boolean>>(DEFAULT_ALERT_CONTROLS)
  const [isLoading, setIsLoading] = useState(false)

  // Load alert controls from API
  useEffect(() => {
    const fetchControls = async () => {
      setIsLoading(true)

      try {
        // TODO: Replace with actual API call from webapp services when available
        // Example: const response = await api.getAlertControls()
        // For now, use defaults from localStorage or DEFAULT_ALERT_CONTROLS
        const saved = localStorage.getItem('alert-controls')
        if (saved) {
          setLocalControls(JSON.parse(saved))
        } else {
          setLocalControls(DEFAULT_ALERT_CONTROLS)
        }
      } catch (error) {
        console.error('Failed to load alert controls:', error)
        setLocalControls(DEFAULT_ALERT_CONTROLS)
      } finally {
        setIsLoading(false)
      }
    }

    fetchControls()
  }, [])

  const handleToggleControl = async (alertType: AlertType, enabled: boolean) => {
    // Optimistically update local state
    const newControls = { ...localControls, [alertType]: enabled }
    setLocalControls(newControls)

    try {
      // TODO: Replace with actual API call from webapp services when available
      // Example: await api.updateAlertControls(alertType, enabled)
      console.log(`Alert control toggle: ${alertType} = ${enabled}`)

      // Save to localStorage as fallback
      localStorage.setItem('alert-controls', JSON.stringify(newControls))
    } catch (error) {
      // Revert on error
      setLocalControls(localControls)
      console.error('Failed to update alert control:', error)
    }
  }

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-medium text-gray-900">Alert Type Controls</h3>
        <p className="mt-1 text-sm text-gray-600">
          Enable or disable different types of alerts globally. When disabled, no alerts of that type will be sent
          regardless of individual settings.
        </p>
      </div>

      {/* Alert Controls */}
      <div className="space-y-4">
        {Object.values(AlertType).map((alertType) => {
          const info = alertTypeInfo[alertType]
          const isEnabled = localControls[alertType]

          return (
            <div
              key={alertType}
              className="flex items-start space-x-4 p-4 border border-gray-200 rounded-lg hover:shadow-sm transition-shadow"
            >
              <div className={`p-2 rounded-full bg-gray-100 ${info.color}`}>
                <FontAwesomeIcon icon={info.icon} className="h-5 w-5" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">{info.title}</h4>
                    <p className="text-sm text-gray-600 mt-1">{info.description}</p>
                  </div>

                  <button
                    onClick={() => handleToggleControl(alertType, !isEnabled)}
                    className={`
                      flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors
                      ${
                        isEnabled
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }
                    `}
                  >
                    <FontAwesomeIcon
                      icon={isEnabled ? faToggleOn : faToggleOff}
                      className={`h-4 w-4 ${isEnabled ? 'text-green-600' : 'text-gray-400'}`}
                    />
                    <span>{isEnabled ? 'Enabled' : 'Disabled'}</span>
                  </button>
                </div>

                {isEnabled && (
                  <div className="mt-2 text-xs text-green-700 bg-green-50 rounded px-2 py-1 inline-block">
                    Active - alerts of this type will be sent based on individual settings
                  </div>
                )}

                {!isEnabled && (
                  <div className="mt-2 text-xs text-gray-600 bg-gray-50 rounded px-2 py-1 inline-block">
                    Inactive - no alerts of this type will be sent
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <div className="text-sm text-blue-700">
          <h4 className="font-medium mb-2">Alert Control Summary:</h4>
          <ul className="space-y-1 text-xs">
            {Object.entries(localControls).map(([type, enabled]) => (
              <li key={type} className="flex items-center space-x-2">
                <FontAwesomeIcon
                  icon={enabled ? faToggleOn : faToggleOff}
                  className={enabled ? 'text-green-600' : 'text-gray-400'}
                />
                <span>
                  {alertTypeInfo[type as AlertType].title}: {enabled ? 'Active' : 'Disabled'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Warning */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
        <div className="flex">
          <FontAwesomeIcon icon={faWarning} className="h-5 w-5 text-yellow-400 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">Important Note</h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>
                Disabling an alert type here will prevent ALL alerts of that type from being sent, regardless of your
                individual alert settings. Use this for temporary maintenance or to disable entire categories of
                notifications.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
